import React from 'react';
import { Link } from 'react-router-dom';
import amplifyService from '../services/amplify.service.ts';

type Leader = {
  id: string;
  name?: string | null;
  score?: number | null;
  count?: number | null;
  time?: number | null;
  lastSolvedAt?: number | null;
};

function Leaderboard() {
  const [leaders, setLeaders] = React.useState<Leader[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [isMobile, setIsMobile] = React.useState<boolean>(window.innerWidth < 768);
  const [leaderboardType, setLeaderboardType] = React.useState<'all' | 'latest'>('all');

  const [sortColumn, setSortColumn] = React.useState<'score' | 'count' | 'time'>('score');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      const data = leaderboardType === 'all'
        ? await amplifyService.listUsersByScore()
        : await amplifyService.listUsersByLatestPuzzles();
      setLeaders(data);
      setLoading(false);
    };
    load();
  }, [leaderboardType]);

  const handleSort = (column: 'score' | 'count' | 'time') => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection(column === 'count' ? 'desc' : 'asc');
    }
  };

  const renderSortIcon = (column: 'score' | 'count' | 'time') => {
    const isDescDefault = column === 'score' || column === 'count';
    const isActive = sortColumn === column;
    const arrow = isActive
      ? (sortDirection === 'asc' ? '↑' : '↓')
      : (isDescDefault ? '↓' : '↑');

    return (
      <span style={{
        fontSize: '1.1rem',
        marginLeft: '2px',
        opacity: isActive ? 1 : 0.25,
        transition: 'all 0.2s',
        display: 'inline-block',
        lineHeight: 0.8,
        verticalAlign: 'middle',
        color: isActive ? '#60a5fa' : '#a3bffa'
      }}>
        {arrow}
      </span>
    );
  };

  const sortedLeaders = React.useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    const fiveDaysInSeconds = 5 * 24 * 60 * 60;

    return [...leaders].sort((a, b) => {
      // Keep inactive at bottom
      const aInactive = !a.lastSolvedAt || (now - a.lastSolvedAt) > fiveDaysInSeconds || (a.count || 0) === 0;
      const bInactive = !b.lastSolvedAt || (now - b.lastSolvedAt) > fiveDaysInSeconds || (b.count || 0) === 0;

      if (aInactive && !bInactive) return 1;
      if (!aInactive && bInactive) return -1;

      let aVal = 0, bVal = 0;
      if (sortColumn === 'score') {
        aVal = a.score || 0;
        bVal = b.score || 0;
      } else if (sortColumn === 'count') {
        aVal = a.count || 0;
        bVal = b.count || 0;
      } else if (sortColumn === 'time') {
        aVal = a.count && a.count > 0 ? (a.time || 0) / a.count : 0;
        bVal = b.count && b.count > 0 ? (b.time || 0) / b.count : 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [leaders, sortColumn, sortDirection]);

  return (
    <div style={{ width: '100%', maxWidth: '980px' }}>
      <div className="profile-hero" style={{ textAlign: 'center', marginBottom: '1.1rem', paddingBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>Leaderboard</h2>
        <p style={{ marginTop: '0.35rem', marginBottom: '0.9rem', opacity: 0.85 }}>Top players by average score</p>

        {/* Tab Switcher */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setLeaderboardType('all')}
            style={{
              padding: isMobile ? '0.4rem 0.8rem' : '0.5rem 1rem',
              fontSize: isMobile ? '0.75rem' : '0.8rem',
              fontWeight: 600,
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              background: leaderboardType === 'all'
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'rgba(255,255,255,0.05)',
              color: leaderboardType === 'all' ? '#fff' : '#94a3b8',
              boxShadow: leaderboardType === 'all'
                ? '0 3px 10px rgba(102, 126, 234, 0.3)'
                : 'none',
            }}
            onMouseEnter={(e) => {
              if (leaderboardType !== 'all') {
                e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
              }
            }}
            onMouseLeave={(e) => {
              if (leaderboardType !== 'all') {
                e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
              }
            }}
          >
            🏆 All Time
          </button>

          <button
            onClick={() => setLeaderboardType('latest')}
            style={{
              padding: isMobile ? '0.4rem 0.8rem' : '0.5rem 1rem',
              fontSize: isMobile ? '0.75rem' : '0.8rem',
              fontWeight: 600,
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              background: leaderboardType === 'latest'
                ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                : 'rgba(255,255,255,0.05)',
              color: leaderboardType === 'latest' ? '#fff' : '#94a3b8',
              boxShadow: leaderboardType === 'latest'
                ? '0 3px 10px rgba(245, 87, 108, 0.3)'
                : 'none',
            }}
            onMouseEnter={(e) => {
              if (leaderboardType !== 'latest') {
                e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
              }
            }}
            onMouseLeave={(e) => {
              if (leaderboardType !== 'latest') {
                e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
              }
            }}
          >
            🔥 Latest Puzzles
          </button>
        </div>
      </div>

      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1f2937 100%)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '14px',
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '35px 1fr 80px 60px 75px' : '55px 1fr 120px 100px 110px',
          gap: isMobile ? '4px' : '14px',
          padding: isMobile ? '8px 10px' : '10px 14px',
          color: '#a3bffa',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          fontSize: isMobile ? '0.75rem' : '0.875rem',
          fontWeight: 600
        }}>
          <div>#</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0' : '10px' }}>
            {!isMobile && <div style={{ width: 32 }} />}
            <span>Player</span>
          </div>
          <div onClick={() => handleSort('score')} style={{ cursor: 'pointer', userSelect: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '2px' }}>
              Score
              {renderSortIcon('score')}
            </div>
          </div>
          <div onClick={() => handleSort('count')} style={{ cursor: 'pointer', userSelect: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '2px' }}>
              Solved
              {renderSortIcon('count')}
            </div>
          </div>
          <div onClick={() => handleSort('time')} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '2px' }}>
              Avg Time
              {renderSortIcon('time')}
            </div>
          </div>
        </div>

        {sortedLeaders.map((u, idx) => {
          const now = Math.floor(Date.now() / 1000); // Unix timestamp (seconds)
          const fiveDaysInSeconds = 5 * 24 * 60 * 60; // 5 gün
          const isInactive = !u.lastSolvedAt || (now - u.lastSolvedAt) > fiveDaysInSeconds || (u.count || 0) === 0;

          return (
            <div key={u.id} style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '35px 1fr 80px 60px 75px' : '55px 1fr 120px 100px 110px',
              gap: isMobile ? '4px' : '14px',
              padding: isMobile ? '10px' : '12px 14px',
              alignItems: 'center',
              borderBottom: idx === leaders.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.06)',
              fontSize: isMobile ? '0.8rem' : '0.875rem',
              opacity: isInactive ? 0.4 : 1,
              background: isInactive ? 'rgba(60,60,60,0.2)' : 'transparent',
              transition: 'all 0.2s ease',
            }}>
              <div style={{ color: '#94a3b8', fontWeight: 700, fontSize: isMobile ? '0.85rem' : '1rem' }}>
                {loading ? '-' : idx + 1}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0' : '10px' }}>
                {!isMobile && (
                  <div className="avatar" style={{ width: 32, height: 32, fontSize: 14 }}>
                    {loading ? '-' : (u.name || u.id || 'U').toString().charAt(0)}
                  </div>
                )}
                <Link
                  to={`/profile/${u.id}`}
                  style={{
                    color: '#60a5fa',
                    fontWeight: 600,
                    fontSize: isMobile ? '0.8rem' : '0.875rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textDecoration: 'none',
                    cursor: loading ? 'default' : 'pointer',
                    transition: 'color 0.2s ease',
                    pointerEvents: loading ? 'none' : 'auto'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.color = '#93c5fd';
                      e.currentTarget.style.textDecoration = 'underline';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.currentTarget.style.color = '#60a5fa';
                      e.currentTarget.style.textDecoration = 'none';
                    }
                  }}
                >
                  {loading ? '-' : (u.name || u.id || 'Unknown')}
                </Link>
              </div>
              <div style={{
                textAlign: 'right',
                color: '#e5e7eb',
                fontWeight: 700,
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }}>
                {loading ? '-' : ((u.score || 0) * 100).toFixed(1)}
              </div>
              <div style={{
                textAlign: 'right',
                color: '#e5e7eb',
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }}>
                {loading ? '-' : (u.count || 0)}
              </div>
              <div style={{
                textAlign: 'right',
                color: '#e5e7eb',
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }}>
                {loading ? '-' : (u.count && u.count > 0 ? Math.floor((u.time || 0) / u.count) : 0) + 's'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Leaderboard;

